import { Col, Row } from "antd";
import Button from "components/button/Button";
import InputComponent from "components/formInput/Input";
import InputPassword from "components/formInput/InputPassword";
import Loading from "components/loading/Loading";
import { ERROR_MSG } from "constants/Constants";
import { TEXTFIELD_ALLOW } from "constants/regex";
import { useFormik } from "formik";
import { apiGetPostCodeInfo, apiUpdateClinic } from "helpers/api/clinic";
import Cookies from "js-cookie";
import { keys, omit, some } from "lodash";
import { useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { showToastError, showToastSuccess } from "utils";
import * as yup from "yup";
import "./styles.scss";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { ROUTE } from "routes/route";

const ClinicSettingPage = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const userInfoString = Cookies.get("USER_INFO");
  const userInfo = userInfoString ? JSON.parse(userInfoString) : {};

  const {
    values,
    errors,
    setFieldValue,
    setFieldError,
    handleChange,
    validateForm,
  } = useFormik({
    validateOnChange: false,
    initialValues: {
      clinicName: userInfo.clinic_name,
      clinicNameFurigana: userInfo.clinic_name_furigana,
      postalCode: userInfo.address.postal_code,
      province: userInfo.address.province,
      addressName: userInfo.address.name,
      building: userInfo.address.building,
      phoneNumber: userInfo.phone_number,
      email: userInfo.email,
      confirmEmail: userInfo.email,
      changePassword: false,
      password: userInfo.decode_password || "",
      confirmPassword: userInfo.decode_password || "",
    },
    validationSchema: yup.object({
      clinicName: yup
        .string()
        .required(ERROR_MSG.REQUIRE)
        .max(255, t("messages.MSG-USP052", { number: 255 }))
        .test(
          "fullwidth",
          t("messages.MSG-USP101"),
          (value) => !TEXTFIELD_ALLOW.FULL_WIDTH_HIRA_KANA_KANJI.test(value)
        ),
      clinicNameFurigana: yup
        .string()
        .required(ERROR_MSG.REQUIRE)
        .max(255, t("messages.MSG-USP052", { number: 255 }))
        .test(
          "fullwidth-furigana",
          t("messages.MSG-USP102"),
          (value) => !TEXTFIELD_ALLOW.FULL_WIDTH_FURIGANA.test(value)
        ),
      postalCode: yup
        .string()
        .required(ERROR_MSG.REQUIRE)
        .max(7, t("messages.MSG-USP052", { number: 7 })),
      province: yup
        .string()
        .required(ERROR_MSG.REQUIRE)
        .max(255, t("messages.MSG-USP052", { number: 255 })),
      addressName: yup
        .string()
        .required(ERROR_MSG.REQUIRE)
        .max(255, t("messages.MSG-USP052", { number: 255 })),
      building: yup
        .string()
        .required(ERROR_MSG.REQUIRE)
        .max(255, t("messages.MSG-USP052", { number: 255 })),
      phoneNumber: yup
        .string()
        .required(ERROR_MSG.REQUIRE)
        .min(10, t("messages.MSG-USP053", { number: 10 }))
        .max(15, t("messages.MSG-USP052", { number: 15 })),
      email: yup
        .string()
        .required(ERROR_MSG.REQUIRE)
        .max(255, t("messages.MSG-USP052", { number: 255 }))
        .email(t("messages.MSG-USP001")),
      confirmEmail: yup
        .string()
        .required(ERROR_MSG.REQUIRE)
        .oneOf([yup.ref("email")], t("messages.MSG-USP015")),
      changePassword: yup.boolean(),
      password: yup
        .string()
        .when("changePassword", (values, schema) =>
          values[0]
            ? yup
              .string()
              .required(ERROR_MSG.REQUIRE)
              .min(8, t("messages.MSG-USP003"))
              .max(36, t("messages.MSG-USP003"))
            : schema
        ),
      confirmPassword: yup.string().when("changePassword", (values, schema) =>
        values[0]
          ? yup
            .string()
            .required(ERROR_MSG.REQUIRE)
            .oneOf([yup.ref("password")], t("messages.MSG-USP011"))
          : schema
      ),
    }),
  });

  const updateClinic = async () => {
    const isValid = Boolean(!keys(await validateForm()).length);
    if (isValid) {
      setIsLoading(true);
      const res = await apiUpdateClinic(userInfo.id, {
        clinic_name: values.clinicName,
        clinic_name_furigana: values.clinicNameFurigana,
        address: {
          postal_code: values.postalCode,
          province: values.province,
          name: values.addressName,
          building: values.building,
        },
        phone_number: values.phoneNumber,
        email: values.email,
        ...(values.changePassword ? { password: values.password } : {}),
      });
      if (res?.data) {
        showToastSuccess("messages.MSG-USP055");
        if (res?.data.decode_password != null && res?.data.decode_password != userInfo.decode_password) {
          console.log("viphoamt data", res?.data);
          localStorage.clear();
          Cookies.remove("USER_INFO");
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          window.location.href = ROUTE.PUBLIC.LOGIN;
          return;
        }
        Cookies.set(
          "USER_INFO",
          JSON.stringify({
            ...res.data,
          })
        );
      }
      if (res?.error?.reason) {
        showToastError(`messages.${res.error.reason}`);
      }
      setIsLoading(false);
    }
  };

  const disableSubmit = useMemo(() => {
    if (values.changePassword) {
      return some(Object.values(values), (value) => Boolean(!value));
    } else {
      return some(
        Object.values(
          omit(values, ["changePassword", "password", "confirmPassword"])
        ),
        (value) => Boolean(!value)
      );
    }
  }, [values]);

  const handleChangePostalCode = () => {
    apiGetPostCodeInfo(values.postalCode, (address) => {
      setFieldValue("province", address?.prefecture);
      setFieldValue("addressName", `${address?.city}${address?.area}`);
    });
  };

  return (
    <div className="clinic-setting main-content">
      <div className="root-right-header">
        <div className="screen-name">{userInfo.clinic_name + " " + t('prefix')}</div>
      </div>
      <div className="nav screen-title">設定</div>
      <div className="content">
        <form>
          <Container>
            <Row gutter={[16, 32]}>
              <Col xs={24} md={24} lg={12}>
                <InputComponent
                  title="ご登録クリニック名"
                  name="clinicName"
                  value={values?.clinicName}
                  error={errors?.clinicName}
                  onChange={handleChange}
                />
                <InputComponent
                  title="ご登録クリニック名（ふりがな）"
                  name="clinicNameFurigana"
                  value={values?.clinicNameFurigana}
                  error={errors?.clinicNameFurigana}
                  onChange={handleChange}
                />
                <Row className="clinic-row">
                  <InputComponent
                    title="ご登録クリニック住所"
                    prefixOut={<span className="post-code-icon">〒</span>}
                    name="postalCode"
                    value={values?.postalCode}
                    error={errors?.postalCode}
                    onChange={handleChange}
                  />
                  <div className="clinic-search-btn">
                    <Button
                      type="button"
                      text="住所検索"
                      background="#0687BB"
                      onClick={handleChangePostalCode}
                    />
                  </div>
                </Row>
                <InputComponent
                  name="province"
                  value={values?.province}
                  error={errors?.province}
                  onChange={handleChange}
                />
                <InputComponent
                  name="addressName"
                  value={values?.addressName}
                  error={errors?.addressName}
                  onChange={handleChange}
                />
                <InputComponent
                  name="building"
                  value={values?.building}
                  error={errors?.building}
                  onChange={handleChange}
                />
                <InputComponent
                  title="ご登録クリニック電話番号（半角）"
                  name="phoneNumber"
                  value={values?.phoneNumber}
                  error={errors?.phoneNumber}
                  onChange={handleChange}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Col>
              <Col xs={24} md={24} lg={12}>
                <InputComponent
                  title="代表メールアドレス"
                  name="email"
                  value={values?.email}
                  error={errors?.email}
                  onChange={handleChange}
                />
                <InputComponent
                  des="確認用"
                  name="confirmEmail"
                  value={values?.confirmEmail}
                  error={errors?.confirmEmail}
                  onChange={handleChange}
                />
                <InputPassword
                  type="password"
                  title="パスワード（半角英数字8文字以上）"
                  placeholder="********"
                  name="password"
                  value={values?.password}
                  error={errors?.password}
                  onChange={handleChange}
                  disabled={!values.changePassword}
                />
                <InputPassword
                  type="confirmPassword"
                  des="確認用"
                  name="confirmPassword"
                  placeholder="********"
                  value={values?.confirmPassword}
                  error={errors?.confirmPassword}
                  onChange={handleChange}
                  disabled={!values.changePassword}
                />
                <div className="password-toggle-btn">
                  <Button
                    type="button"
                    text="パスワードの変更"
                    background="#0687BB"
                    onClick={() => {
                      setFieldValue("changePassword", !values.changePassword);
                    }}
                  />
                </div>
              </Col>
            </Row>
          </Container>

          <Button
            text="保存"
            width={400}
            height={40}
            disabled={disableSubmit}
            style={{
              fontSize: 20,
              fontWeight: 900,
              borderRadius: 60,
              marginTop: 50,
            }}
            onClick={(e) => {
              e.preventDefault();
              updateClinic();
            }}
          />
        </form>
      </div>
      {isLoading && <Loading />}
    </div>
  );
};

export default ClinicSettingPage;
